/**
 * v0.9 玩家回饋（異常／需求）：遊戲前端、Cloudflare Pages Function（functions/api/feedback.ts）、
 * 同步腳本（scripts/feedback-pull.ts）共用的格式、檢查與「存成哪些檔案」。
 *
 * 存放位置（R2 bucket，binding 名稱 FEEDBACK）：
 *   feedback/bug/<id>/   report.md（給人看）、report.json（完整資料）、image-1.jpg…
 *   feedback/idea/<id>/  report.md、report.json、mission-draft.json（照任務格式的草稿）、image-1.jpg…
 * 同步到電腦後：玩家回饋/異常/<id>/…、玩家回饋/需求/<id>/…
 *
 * 這個檔案不能用 DOM、不能用 tr()（Cloudflare 端也要跑）。錯誤用代碼回傳，畫面再翻成文字。
 */
export type FeedbackKind = 'bug' | 'idea';

export interface IdeaStep { floor: string; where: string; action: string; say: string }
export interface IdeaChoice { situation: string; good: string; bad: string; result: string }
export interface Idea {
  title: string;
  /** main 主線／side 支線／daily 每日／accident 突發事件・事故／case 異常案件／system 新玩法・系統／other 其他 */
  type: 'main' | 'side' | 'daily' | 'accident' | 'case' | 'system' | 'other';
  /** 職務 id 或 'any' */
  role: string;
  floor: string;
  steps: IdeaStep[];
  choice?: IdeaChoice;
  lesson?: string;
}
export interface FeedbackPayload {
  kind: FeedbackKind;
  /** 異常：說明（必填）；需求：故事背景（必填） */
  text: string;
  email?: string;
  /** data:image/jpeg;base64,…（前端先壓縮過） */
  images: string[];
  idea?: Idea;
  /** 遊戲狀態：版本、語言、職務、樓層、位置、時間、最近的紀錄 */
  context?: Record<string, unknown>;
  /** 防機器人：真人看不到這一欄，有填就丟掉 */
  website?: string;
}
export type FeedbackError = 'kind' | 'text' | 'image' | 'imageType' | 'imageSize' | 'tooMany' | 'email' | 'title' | 'tooBig' | 'spam';

export const LIMITS = { text: 4000, images: 3, imageChars: 2_000_000, body: 7_000_000, title: 80, stepText: 300, steps: 12 };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const IMG = /^data:image\/(jpeg|png|webp|gif);base64,[A-Za-z0-9+/=]+$/;

export function validateFeedback(p: Partial<FeedbackPayload>): FeedbackError[] {
  const e: FeedbackError[] = [];
  if (p.website) e.push('spam');
  if (p.kind !== 'bug' && p.kind !== 'idea') { e.push('kind'); return e; }
  const text = (p.text ?? '').trim();
  if (text.length < 5 || text.length > LIMITS.text) e.push('text');
  const imgs = p.images ?? [];
  if (p.kind === 'bug' && imgs.length < 1) e.push('image');
  if (imgs.length > LIMITS.images) e.push('tooMany');
  if (imgs.some(i => typeof i !== 'string' || !IMG.test(i.slice(0, 64) + 'A'))) e.push('imageType');
  if (imgs.some(i => i.length > LIMITS.imageChars)) e.push('imageSize');
  if (p.email && !EMAIL.test(p.email.trim())) e.push('email');
  if (p.kind === 'idea') {
    const t = (p.idea?.title ?? '').trim();
    if (t.length < 2 || t.length > LIMITS.title) e.push('title');
    if ((p.idea?.steps?.length ?? 0) > LIMITS.steps) e.push('tooMany');
  }
  return e;
}

/** 回饋編號：2026-10-14_093012_ab12（時間＋亂數，排序＝時間順序） */
export function feedbackId(now = new Date(), rand = Math.random): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const d = new Date(now.getTime() + 8 * 3600_000);   // 台灣時間
  const r = Math.floor(rand() * 36 ** 4).toString(36).padStart(4, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}_${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}_${r}`;
}

const TYPE_NAME: Record<Idea['type'], string> = { main: '主線任務', side: '支線任務', daily: '每日任務', accident: '突發事件／事故', case: '異常案件', system: '新玩法／系統', other: '其他' };
const clean = (s: unknown, max = LIMITS.stepText) => String(s ?? '').replace(/\r/g, '').slice(0, max).trim();

/** 整理過、去掉多餘欄位的資料（存進 report.json） */
export function normalize(p: FeedbackPayload, id: string, received: string): Record<string, unknown> {
  const idea = p.idea ? {
    title: clean(p.idea.title, LIMITS.title), type: p.idea.type in TYPE_NAME ? p.idea.type : 'other', role: clean(p.idea.role, 40), floor: clean(p.idea.floor, 10),
    steps: (p.idea.steps ?? []).slice(0, LIMITS.steps).map(s => ({ floor: clean(s.floor, 10), where: clean(s.where), action: clean(s.action), say: clean(s.say) })).filter(s => s.where || s.action || s.say),
    choice: p.idea.choice && (p.idea.choice.situation || p.idea.choice.good) ? { situation: clean(p.idea.choice.situation), good: clean(p.idea.choice.good), bad: clean(p.idea.choice.bad), result: clean(p.idea.choice.result) } : undefined,
    lesson: clean(p.idea.lesson),
  } : undefined;
  return { id, kind: p.kind, received, text: clean(p.text, LIMITS.text), email: p.email ? clean(p.email, 120) : '', images: p.images.length, idea, context: p.context ?? {} };
}

/** 照遊戲任務格式（content/missions/）的草稿：開發時拿來改 */
export function missionDraft(n: Record<string, any>): Record<string, unknown> {
  const i = n.idea; if (!i) return {};
  return {
    id: `M-idea-${String(n.id).slice(0, 17).replace(/[-_]/g, '')}`, type: ['main', 'side', 'daily'].includes(i.type) ? i.type : 'side',
    title: i.title, icon: '💡', summary: String(n.text).split('\n')[0].slice(0, 80),
    ...(i.role && i.role !== 'any' ? { for: { roles: [i.role] } } : {}),
    steps: (i.steps.length ? i.steps : [{ floor: i.floor, where: '', action: '', say: n.text }]).map((s: IdeaStep, k: number) => ({
      floor: s.floor || i.floor || '1F', target: `TODO:${s.where || '地點'}`, do: 'use', say: s.action || s.say,
      ...(s.say ? { hint: s.say } : {}),
      ...(k === i.steps.length - 1 && i.choice ? { choose: { question: i.choice.situation, options: [{ text: i.choice.good, good: true, result: i.choice.result }, { text: i.choice.bad, good: false, result: '' }] } } : {}),
    })),
    reward: { text: i.lesson || '完成了！' },
    _note: '玩家提出的想法草稿：target 要換成地圖上的設備 id，do 要換成正確的動作，再放進 content/missions/。',
  };
}

/** 給人看的 report.md */
export function reportMarkdown(n: Record<string, any>): string {
  const L: string[] = [];
  const ctx = n.context ?? {};
  const ctxLine = ['version', 'contentVersion', 'lang', 'role', 'floor', 'zone', 'clock', 'day'].filter(k => ctx[k] != null).map(k => `${k}: ${ctx[k]}`).join('・');
  if (n.kind === 'bug') {
    L.push(`# 🐞 異常回報 ${n.id}`, '', `- 收到時間：${n.received}`, `- Email：${n.email || '（沒填）'}`, `- 圖片：${n.images} 張（image-1…）`, ctxLine ? `- 遊戲狀態：${ctxLine}` : '', '', '## 說明', '', n.text, '');
    if (Array.isArray(ctx.log) && ctx.log.length) L.push('## 最近的遊戲紀錄', '', ...ctx.log.map((x: string) => `- ${x}`), '');
    if (ctx.ua) L.push(`瀏覽器：${ctx.ua}`, '');
  } else {
    const i = n.idea ?? {};
    L.push(`# 💡 玩家想法：${i.title ?? ''}`, '', `- 編號：${n.id}`, `- 收到時間：${n.received}`, `- 類型：${TYPE_NAME[i.type as Idea['type']] ?? i.type}`, `- 職務：${!i.role || i.role === 'any' ? '不限' : i.role}`, `- 樓層：${!i.floor || i.floor === 'any' ? '不限' : i.floor}`, `- Email：${n.email || '（沒填）'}`, `- 圖片：${n.images} 張`, ctxLine ? `- 遊戲狀態：${ctxLine}` : '', '', '## 故事背景', '', n.text, '');
    if (i.steps?.length) { L.push('## 步驟', ''); i.steps.forEach((s: IdeaStep, k: number) => L.push(`${k + 1}. 【${s.floor || '?'}・${s.where || '?'}】${s.action}${s.say ? `\n   💬「${s.say}」` : ''}`)); L.push(''); }
    if (i.choice) L.push('## 選擇題', '', `- 情境：${i.choice.situation}`, `- ✅ 好的做法：${i.choice.good}`, `- ❌ 不好的做法：${i.choice.bad}`, `- 結果：${i.choice.result}`, '');
    if (i.lesson) L.push('## 玩家會學到', '', i.lesson, '');
    L.push('---', '要做的話：跟 Claude 說「看玩家回饋 需求 ' + n.id + '」，它會照 mission-draft.json 改成正式任務。');
  }
  return L.filter((x, k, a) => !(x === '' && a[k - 1] === '')).join('\n') + '\n';
}

const EXT: Record<string, string> = { jpeg: 'jpg', png: 'png', webp: 'webp', gif: 'gif' };
function b64(s: string): Uint8Array {
  if (typeof atob === 'function') { const bin = atob(s); const out = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i); return out; }
  return new Uint8Array(Buffer.from(s, 'base64'));
}
/** 一筆回饋要存成哪些檔案（路徑相對於這筆回饋的資料夾） */
export function feedbackFiles(p: FeedbackPayload, id: string, received: string): { path: string; type: string; body: Uint8Array | string }[] {
  const n = normalize(p, id, received);
  const files: { path: string; type: string; body: Uint8Array | string }[] = [
    { path: 'report.md', type: 'text/markdown; charset=utf-8', body: reportMarkdown(n) },
    { path: 'report.json', type: 'application/json; charset=utf-8', body: JSON.stringify(n, null, 1) },
  ];
  if (p.kind === 'idea') files.push({ path: 'mission-draft.json', type: 'application/json; charset=utf-8', body: JSON.stringify(missionDraft(n), null, 1) });
  p.images.forEach((img, k) => {
    const m = img.match(/^data:image\/(\w+);base64,(.*)$/s); if (!m) return;
    files.push({ path: `image-${k + 1}.${EXT[m[1]] ?? 'img'}`, type: `image/${m[1]}`, body: b64(m[2]) });
  });
  return files;
}
/** R2 裡的資料夾：feedback/bug/<id>/ */
export const feedbackPrefix = (kind: FeedbackKind, id: string) => `feedback/${kind}/${id}/`;
/** 同步到電腦後的資料夾名稱 */
export const LOCAL_DIR: Record<FeedbackKind, string> = { bug: '異常', idea: '需求' };
